import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { List, ListItem, Member } from '@pacer/shared';
import * as api from '../api';
import { CheckToggle } from '../components/CheckToggle';
import { Icon } from '../components/Icon';
import { QuickAdd } from '../components/QuickAdd';
import { Avatar, Badge, Button, Card, EmptyState, IconButton, PageHeader, Spinner, cx } from '../components/ui';
import { groupByAisle } from '../lib/grocery';
import { confettiBig } from '../state/celebrate';
import { useActiveMemberId } from '../state/activeMember';
import { queryKeys } from '../state/queries';
import { toast } from '../state/ui';

export function Lists() {
  const { data: lists = [], isLoading } = useQuery({ queryKey: queryKeys.lists, queryFn: api.getLists });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });
  const [tab, setTab] = useState<'todo' | 'shopping'>('todo');

  if (isLoading) return <Spinner label="loading" />;

  const shown = lists.filter((l) => l.type === tab);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10">
      <PageHeader
        eyebrow="Shared"
        title={tab === 'todo' ? 'To-do' : 'Shopping'}
        actions={
          <div className="inline-flex items-center gap-0.5 rounded-md border border-border-subtle bg-surface p-0.5">
            {(['todo', 'shopping'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cx(
                  'h-9 rounded-[5px] px-3 text-[13px] transition-colors duration-150',
                  tab === t
                    ? 'bg-[var(--color-accent-dim)] text-accent'
                    : 'text-muted hover:bg-surface-high hover:text-secondary',
                )}
              >
                {t === 'todo' ? 'To-do' : 'Shopping'}
              </button>
            ))}
          </div>
        }
      />

      {shown.length === 0 ? (
        <EmptyState title="No lists yet" hint="Lists are created for you on first run." />
      ) : (
        <div className="flex flex-col gap-8">
          {shown.map((list) => (
            <ListPanel key={list.id} list={list} members={members} />
          ))}
        </div>
      )}
    </div>
  );
}

function ListPanel({ list, members }: { list: List; members: Member[] }) {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const [showPantry, setShowPantry] = useState(false);
  const isShopping = list.type === 'shopping';

  const { data: suggestions = [] } = useQuery({
    queryKey: ['suggestions', list.id],
    queryFn: () => api.getSuggestions(list.id),
  });

  const refresh = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: queryKeys.lists });
    await qc.invalidateQueries({ queryKey: ['suggestions', list.id] });
  };

  // Optimistic mutations against the shared lists cache — a checkbox or stepper that waits
  // on a round-trip feels broken under a finger.
  const patchCache = (id: string, patch: Partial<ListItem>): List[] | undefined => {
    const prev = qc.getQueryData<List[]>(queryKeys.lists);
    qc.setQueryData<List[]>(queryKeys.lists, (old) =>
      old?.map((l) =>
        l.id !== list.id ? l : { ...l, items: l.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) },
      ),
    );
    return prev;
  };

  const add = useMutation({
    mutationFn: (text: string) => api.addListItem(list.id, text, { createdBy: activeId }),
    onSuccess: refresh,
  });

  const toggle = useMutation({
    mutationFn: (item: ListItem) => api.updateListItem(item.id, { done: !item.done, doneBy: activeId }),
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists });
      return { prev: patchCache(item.id, { done: !item.done }) };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(queryKeys.lists, ctx.prev),
    onSettled: refresh,
  });

  const setQty = useMutation({
    mutationFn: ({ id, quantity }: { id: string; quantity: number }) =>
      api.updateListItem(id, { quantity }),
    onMutate: async ({ id, quantity }) => {
      await qc.cancelQueries({ queryKey: queryKeys.lists });
      return { prev: patchCache(id, { quantity }) };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(queryKeys.lists, ctx.prev),
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteListItem(id),
    onSuccess: refresh,
  });

  const clear = useMutation({
    mutationFn: () => api.clearDone(list.id),
    onSuccess: refresh,
  });

  const needed = list.items.filter((i) => !i.done);
  const got = list.items.filter((i) => i.done);

  // "Usuals" = your most-bought items not currently on the list. Restocking un-checks a dormant
  // one if we have it, otherwise adds it fresh — one tap to load the weekly staples.
  const neededTexts = new Set(needed.map((i) => i.text.toLowerCase()));
  const gotByText = new Map(got.map((i) => [i.text.toLowerCase(), i] as const));
  const usuals = isShopping ? suggestions.filter((s) => !neededTexts.has(s.toLowerCase())).slice(0, 10) : [];
  const addUsuals = (): void =>
    usuals.forEach((t) => {
      const dormant = gotByText.get(t.toLowerCase());
      if (dormant) toggle.mutate(dormant);
      else add.mutate(t);
    });

  // Celebrate the moment the last thing gets ticked off (there was something, now nothing).
  const prevNeeded = useRef(needed.length);
  useEffect(() => {
    if (prevNeeded.current > 0 && needed.length === 0 && list.items.length > 0) {
      confettiBig();
      toast(isShopping ? 'Shopping done!' : `${list.name} all done!`, 'success');
    }
    prevNeeded.current = needed.length;
  }, [needed.length, list.items.length, isShopping, list.name]);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <h2 className="font-display text-[20px] text-primary">{list.name}</h2>
        <Badge>
          {needed.length} {isShopping ? 'to buy' : 'open'}
        </Badge>
        {/* To-dos are one-shot, so "clear done" deletes them. Shopping items are a catalog —
            they never delete on check, they go to the pantry below. */}
        {!isShopping && got.length > 0 && (
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => clear.mutate()}>
            Clear {got.length} done
          </Button>
        )}
      </div>

      <QuickAdd
        placeholder={isShopping ? 'Add an item…' : 'Add a to-do…'}
        onAdd={(text) => add.mutate(text)}
        suggestions={suggestions.slice(0, 12)}
        busy={add.isPending}
      />

      {isShopping && usuals.length > 0 && (
        <button
          onClick={addUsuals}
          className="flex items-center gap-2 self-start rounded-full border border-border-subtle px-3 py-1.5 text-[13px] text-secondary transition-colors hover:border-border hover:text-primary"
        >
          <Icon name="repeat" size={13} />
          Add usuals ({usuals.length})
        </button>
      )}

      <Card className="overflow-hidden">
        {needed.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-muted">
            {isShopping ? 'Nothing to buy right now.' : 'Nothing on this list.'}
          </p>
        ) : isShopping ? (
          groupByAisle(needed, (i) => i.text).map(({ aisle, items }) => (
            <div key={aisle.key}>
              <div className="border-b border-border-subtle bg-surface-high/40 px-3 py-1.5">
                <span className="eyebrow">{aisle.label}</span>
              </div>
              <ul>
                {items.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    members={members}
                    showQuantity
                    onToggle={() => toggle.mutate(item)}
                    onQty={(q) => setQty.mutate({ id: item.id, quantity: q })}
                    onDelete={() => remove.mutate(item.id)}
                  />
                ))}
              </ul>
            </div>
          ))
        ) : (
          <ul>
            {needed.map((item) => (
              <Row
                key={item.id}
                item={item}
                members={members}
                showQuantity={false}
                onToggle={() => toggle.mutate(item)}
                onQty={(q) => setQty.mutate({ id: item.id, quantity: q })}
                onDelete={() => remove.mutate(item.id)}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* Shopping's dormant catalog — things we have, one tap to put back on the list. */}
      {isShopping && got.length > 0 && (
        <div>
          <button
            onClick={() => setShowPantry((v) => !v)}
            className="flex w-full items-center gap-2 py-1 text-left"
          >
            <Icon
              name="chevron-down"
              size={13}
              className={cx('text-muted transition-transform', !showPantry && '-rotate-90')}
            />
            <span className="eyebrow">In the kitchen · {got.length}</span>
            <span className="ml-auto font-mono text-[12px] text-muted">tap to add back</span>
          </button>

          {showPantry && (
            <Card className="mt-1 overflow-hidden">
              <ul>
                {got.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    members={members}
                    showQuantity={false}
                    onToggle={() => toggle.mutate(item)}
                    onQty={() => {}}
                    onDelete={() => remove.mutate(item.id)}
                  />
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}

function Row({
  item,
  members,
  showQuantity,
  onToggle,
  onQty,
  onDelete,
}: {
  item: ListItem;
  members: Member[];
  showQuantity: boolean;
  onToggle: () => void;
  onQty: (quantity: number) => void;
  onDelete: () => void;
}) {
  const by = members.find((m) => m.id === (item.done ? item.doneBy : item.assignedMemberId));

  return (
    <li
      className={cx(
        // Checked items shrink and fade so the eye slides past what's already handled and
        // lands on what's still open. The transition makes checking feel like the item
        // settling out of the way.
        'group flex items-center gap-2 border-b border-border-subtle px-3 transition-all duration-200 last:border-b-0 hover:bg-surface-high/40',
        item.done ? 'py-0.5 opacity-55' : 'py-0',
      )}
    >
      {/* The whole left region is the hit target — a 20px checkbox is not touchable. */}
      <button
        onClick={onToggle}
        className={cx('flex flex-1 items-center gap-3 text-left transition-all', item.done ? 'py-1' : 'touch py-1.5')}
        aria-pressed={item.done}
      >
        <CheckToggle checked={item.done} size={item.done ? 18 : 24} color={by?.color ?? 'var(--color-accent)'} />
        <span
          className={cx(
            'flex-1 transition-all duration-200',
            item.done ? 'text-[13px] text-muted line-through' : 'text-[15px] text-primary',
          )}
        >
          {item.text}
          {item.source === 'agent' && (
            <span className="ml-2 text-[11px] font-normal text-muted" title="Added by Exponent">
              via Exponent
            </span>
          )}
          {item.source !== 'agent' && item.agentReactivatedAt && (
            <span className="ml-2 text-[11px] font-normal text-muted" title="Exponent put this back on the list">
              re-added by Exponent
            </span>
          )}
          {item.quantity > 1 && (
            <span className={cx('ml-2 font-mono', item.done ? 'text-muted' : 'text-accent')}>×{item.quantity}</span>
          )}
        </span>
      </button>

      {showQuantity && !item.done && (
        <Stepper value={item.quantity} onChange={onQty} />
      )}

      {by && <Avatar name={by.name} color={by.color} photoUrl={by.avatarUrl} size={item.done ? 18 : 24} />}

      <IconButton
        icon="trash"
        label={`Delete ${item.text}`}
        size="sm"
        onClick={onDelete}
        className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
      />
    </li>
  );
}

/** Quantity − N + control. Clamped to 1..99; touch-sized buttons. */
function Stepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="flex items-center overflow-hidden rounded-md border border-border-subtle">
      <button
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        className="flex size-8 items-center justify-center text-secondary transition-colors hover:bg-surface-high disabled:opacity-30"
        aria-label="Fewer"
      >
        <Icon name="minus" size={12} />
      </button>
      <span className="tabular w-6 text-center font-mono text-[13px] text-primary">{value}</span>
      <button
        onClick={() => onChange(Math.min(99, value + 1))}
        className="flex size-8 items-center justify-center text-secondary transition-colors hover:bg-surface-high"
        aria-label="More"
      >
        <Icon name="plus" size={12} />
      </button>
    </div>
  );
}
